import { useState } from "react";
import { Alert, Grid } from "@mui/material";
import { AnswerOption } from "../../API";
import { useTranslation } from "react-i18next";
import {
  FormState,
  UseFieldArrayRemove,
  UseFormGetValues,
  UseFormRegister
} from "react-hook-form";
import { AnswerOptionErrors } from "../Instruments/instrumentUtils";
import { InstrumentWithoutAuthor } from "../Instruments/EditInstrument";

interface EditAnswerOptionProps {
  answerOption: AnswerOption;
  anyApiIsLoading: boolean;
  isAssessmentPublished: boolean;
  register: UseFormRegister<InstrumentWithoutAuthor>;
  questionIndex: number;
  answerOptionIndex: number;
  getValues: UseFormGetValues<InstrumentWithoutAuthor>;
  removeAnswerOption: UseFieldArrayRemove;
  formState: FormState<InstrumentWithoutAuthor>;
}

export const EditAnswerOption = ({
  answerOption,
  anyApiIsLoading,
  isAssessmentPublished,
  register,
  questionIndex,
  answerOptionIndex,
  getValues,
  removeAnswerOption,
  formState
}: EditAnswerOptionProps) => {
  const { t } = useTranslation();
  const [helpTextPreview, setHelpTextPreview] = useState(false);
  const { errors } = formState;
  return (
    <section className="section" key={answerOption.id}>
      <Grid xs={8}>
        <li>
          <label htmlFor={`answerOption-${answerOption.id}-sortingIndex`}>
            {t("Sorting Index")}
          </label>
          <input
            type="number"
            required
            placeholder="0"
            style={{ width: "100%" }}
            id={`answerOption=${answerOption.id}-sortingIndex`}
            disabled={anyApiIsLoading || isAssessmentPublished}
            {...register(
              `questions.items.${questionIndex}.answerOptions.items.${answerOptionIndex}.sortingIndex` as const,
              {
                required: {
                  value: true,
                  message: "AnswerOptionErrors.NoSortingIndex"
                }
              }
            )}
          />
          {errors.questions?.items?.[questionIndex]?.answerOptions?.items?.[
            answerOptionIndex
          ]?.sortingIndex?.message && (
            <Alert severity="error">
              {t(
                errors.questions?.items?.[questionIndex]?.answerOptions
                  ?.items?.[answerOptionIndex]?.sortingIndex?.message || ""
              )}
            </Alert>
          )}

          <label htmlFor={`answerOption-${answerOption.id}-anchor`}>
            {t("Anchor")}
          </label>
          <input
            type="text"
            required
            placeholder="Anchor Text"
            style={{ width: "100%" }}
            id={`answerOption-${answerOption.id}-anchor`}
            disabled={anyApiIsLoading || isAssessmentPublished}
            {...register(
              `questions.items.${questionIndex}.answerOptions.items.${answerOptionIndex}.anchor` as const,
              {
                required: {
                  value: true,
                  message: AnswerOptionErrors.NoAnchor
                }
              }
            )}
          />
          {errors.questions?.items?.[questionIndex]?.answerOptions?.items?.[
            answerOptionIndex
          ]?.anchor?.message && (
            <Alert severity="error">
              {t(
                errors.questions?.items?.[questionIndex]?.answerOptions
                  ?.items?.[answerOptionIndex]?.anchor?.message || ""
              )}
            </Alert>
          )}

          <label htmlFor={`answerOption-${answerOption.id}-value`}>
            {t("Value")}
          </label>
          <input
            type="text"
            required
            placeholder="Value"
            style={{ width: "100%" }}
            id={`answerOption-${answerOption.id}-value`}
            disabled={anyApiIsLoading || isAssessmentPublished}
            {...register(
              `questions.items.${questionIndex}.answerOptions.items.${answerOptionIndex}.value` as const,
              {
                required: {
                  value: true,
                  message: AnswerOptionErrors.NoValue
                }
              }
            )}
          />
          {errors.questions?.items?.[questionIndex]?.answerOptions?.items?.[
            answerOptionIndex
          ]?.value?.message && (
            <Alert severity="error">
              {t(
                errors.questions?.items?.[questionIndex]?.answerOptions
                  ?.items?.[answerOptionIndex]?.value?.message || ""
              )}
            </Alert>
          )}

          <label htmlFor={`answerOption-${answerOption.id}-helpText`}>
            {t("Help Text")}
          </label>
          <div>
            <input
              id={`answerOption-${answerOption.id}-helpTextPreview`}
              type="checkbox"
              checked={helpTextPreview}
              onChange={() => setHelpTextPreview(!helpTextPreview)}
            />
            <label
              htmlFor={`answerOption-${answerOption.id}-helpTextPreview`}
            >{`Mode: ${helpTextPreview ? "Preview" : "Input"}`}</label>
          </div>
          {helpTextPreview ? (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  getValues(
                    `questions.items.${questionIndex}.answerOptions.items.${answerOptionIndex}.helpText`
                  ) || ""
              }}
            />
          ) : (
            <textarea
              placeholder="Help Text"
              style={{ width: "100%" }}
              id={`answerOption-${answerOption.id}-value`}
              disabled={anyApiIsLoading || isAssessmentPublished}
              {...register(
                `questions.items.${questionIndex}.answerOptions.items.${answerOptionIndex}.helpText` as const
              )}
            />
          )}
          <button
            type="button"
            disabled={anyApiIsLoading || isAssessmentPublished}
            onClick={() => removeAnswerOption(answerOptionIndex)}
          >
            {t("Delete Answer Option")}
          </button>
        </li>
      </Grid>
    </section>
  );
};
